import { PodiumRankMedal } from '@/components/social/leaderboard/podium-rank-medal';
import { Skeleton } from '@/components/ui/app-skeleton';
import { cn } from '@/lib/ui/utils';

// Mirrors the visible footprint of `<ProfileCard>` so the loading state
// reserves the same vertical space the real cards will fill.
//
// Spec: 071-social-podium-cards (FR-023)

interface PodiumSkeletonCardProps {
  /** Rank-1 lifts the card on desktop to match the elevation in the real podium. */
  rank: 1 | 2 | 3;
  /** Mobile track shrink-wraps each card; desktop matches centred flex podium. */
  mobile?: boolean;
}

function PodiumSkeletonCard({ rank, mobile }: PodiumSkeletonCardProps): React.ReactNode {
  return (
    <div
      aria-hidden
      className={cn(
        'relative flex flex-col overflow-hidden rounded-card border border-border-primary bg-background-primary shadow-elevation-card',
        mobile === true
          ? 'w-64 shrink-0 snap-center'
          : 'min-w-0 w-full max-w-[224px] shrink-0 md:w-[224px] lg:max-w-[300px] lg:w-[300px]', // ARBITRARY: spec 071
        rank === 1 ? 'md:-translate-y-8' : '',
      )}
    >
      {/* Hero strip + rank medal (matches `<ProfileCard>` hero overlay). */}
      <div className="relative w-full">
        <Skeleton className="aspect-3/1 w-full" />
        <PodiumRankMedal rank={rank} alt="" />
      </div>
      {/* Avatar placeholder — circle overlapping hero/body junction. */}
      <div className="-mt-10 flex justify-center px-4">
        <Skeleton className="size-20 rounded-full ring-4 ring-background-primary" />
      </div>
      {/* Identity placeholders — each bar sits in a line-box matching
          `<ProfileCard>`'s name (text-body, 20px) over its handle
          (text-body-lg, 24px) at gap-0.5, so the skeleton reserves the loaded
          card's exact height and the podium doesn't jump on settle. */}
      <div className="mt-2 flex flex-col items-center gap-0.5 px-4">
        <span className="flex h-5 items-center">
          <Skeleton className="h-3.5 w-32" />
        </span>
        <span className="flex h-6 items-center">
          <Skeleton className="h-3 w-20" />
        </span>
      </div>
      {/* KPI row — each cell mirrors a value (text-body-lg, 24px) over a label
          (text-caption, 16px) line-box so the row's height equals the loaded
          card's. */}
      <div className="mt-4 grid grid-cols-3 gap-2 px-4 pb-4">
        {(['primary', 'secondary', 'tertiary'] as const).map((slot) => (
          <div key={slot} className="flex flex-col items-center gap-0.5">
            <span className="flex h-6 items-center">
              <Skeleton className="h-4 w-12" />
            </span>
            <span className="flex h-4 items-center">
              <Skeleton className="h-2.5 w-10" />
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * `<PodiumSkeleton>` — matches the 2-1-3 footprint of `<Podium>` so the
 * social page does not jump on first paint.
 */
export function PodiumSkeleton(): React.ReactNode {
  return (
    <>
      {/* Desktop (≥ md): centred flex row with responsive gap — matches `<Podium>`.
          `md:mt-8` mirrors the live podium so rank-1's lift clears the header. */}
      <div className="hidden md:mt-8 md:flex md:items-end md:justify-center md:gap-4 lg:gap-6">
        <PodiumSkeletonCard rank={2} />
        <PodiumSkeletonCard rank={1} />
        <PodiumSkeletonCard rank={3} />
      </div>
      {/* Mobile track (< md): horizontal scroll, rank order 1-2-3. */}
      <div className="-mx-4 flex gap-6 overflow-x-auto px-4 scrollbar-none md:hidden snap-x snap-mandatory">
        <PodiumSkeletonCard rank={1} mobile />
        <PodiumSkeletonCard rank={2} mobile />
        <PodiumSkeletonCard rank={3} mobile />
      </div>
    </>
  );
}
